import { baseApi, API_TAGS, createEndpointBuilder } from '@/shared/api';
import type { Contact, ContactOption, ContactParams, ContactPayload, OrganizationContactsParams } from '../model/types';

const contactsEndpoint = createEndpointBuilder('/api/v2/contacts');
const organizationsEndpoint = createEndpointBuilder('/api/v2/organizations');

export const contactsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getOrganizationContacts: builder.query<ContactOption[], OrganizationContactsParams>({
      query: ({ orgId, projectId, siteId, q }) => ({
        url: organizationsEndpoint(`/${orgId}/contacts`),
        params: { projectId, siteId, q }
      }),
      providesTags: (result) =>
        result ? [...result.map(({ id }) => ({ type: API_TAGS.CONTACTS, id })), API_TAGS.CONTACTS] : [API_TAGS.CONTACTS]
    }),

    /**
     * The full contact an edit form saves back. `PUT` replaces every field, so a form filled from
     * a stale copy would wipe what someone else saved since. Not cached — every opening of the
     * form reads the contact afresh.
     */
    getContact: builder.query<Contact, { id: string } & ContactParams>({
      query: ({ id, projectId, siteId }) => ({
        url: contactsEndpoint(`/${id}`),
        params: { projectId, siteId }
      }),
      providesTags: (_result, _error, { id }) => [{ type: API_TAGS.CONTACTS, id }],
      keepUnusedDataFor: 0
    }),

    createContact: builder.mutation<Contact, ContactPayload & ContactParams>({
      query: ({ projectId, siteId, ...body }) => ({
        url: contactsEndpoint(''),
        method: 'POST',
        params: { projectId, siteId },
        body
      }),
      invalidatesTags: [API_TAGS.CONTACTS]
    }),

    updateContact: builder.mutation<Contact, { id: string } & ContactPayload & ContactParams>({
      query: ({ id, projectId, siteId, ...body }) => ({
        url: contactsEndpoint(`/${id}`),
        method: 'PUT',
        params: { projectId, siteId },
        body
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: API_TAGS.CONTACTS, id }, API_TAGS.CONTACTS]
    })
  })
});

export const {
  useGetOrganizationContactsQuery,
  useGetContactQuery,
  useCreateContactMutation,
  useUpdateContactMutation
} = contactsApi;
